/**
 * Same-origin, non-authoritative mutex for the pre-auth device bootstrap.
 *
 * This module intentionally persists only an opaque per-tab owner identifier
 * and a short expiry. It never persists a device proof, bearer, CSRF value,
 * account identifier, refresh-family reference, or other session authority.
 */

const DATABASE_NAME = "careerforge-device-bootstrap-mutex";
const DATABASE_VERSION = 1;
const STORE_NAME = "locks";
const LOCK_KEY = "device-bootstrap";
// The caller's bootstrap request has a shorter, explicit Axios timeout. The
// lease therefore cannot expire while a healthy caller is still awaiting that
// request, but an abruptly terminated tab remains recoverable.
const DEFAULT_LEASE_MS = 15_000;
const DEFAULT_TIMEOUT_MS = 10_000;
const DEFAULT_RETRY_MS = 50;

type LockRecord = {
  key: string;
  owner: string;
  expiresAt: number;
};

type IndexedDbFactoryLike = Pick<IDBFactory, "open">;

export type DeviceBootstrapMutexOptions = {
  owner: string;
  leaseMs?: number;
  timeoutMs?: number;
  retryMs?: number;
  indexedDb?: IndexedDbFactoryLike | null;
  now?: () => number;
  sleep?: (milliseconds: number) => Promise<void>;
};

export class DeviceBootstrapUnavailableError extends Error {
  constructor(message = "Device bootstrap is unavailable. Please retry in a supported browser.") {
    super(message);
    this.name = "DeviceBootstrapUnavailableError";
  }
}

function pause(milliseconds: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}

function indexedDbFactory(
  supplied: IndexedDbFactoryLike | null | undefined,
): IndexedDbFactoryLike {
  if (supplied) return supplied;
  try {
    if (typeof indexedDB !== "undefined") return indexedDB;
  } catch {
    // Some privacy contexts throw when the IndexedDB getter is accessed.
  }
  throw new DeviceBootstrapUnavailableError();
}

function openMutexDatabase(factory: IndexedDbFactoryLike): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (error) {
      reject(error);
      return;
    }

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed."));
    request.onblocked = () => reject(new Error("IndexedDB mutex database is blocked."));
  });
}

/**
 * Read and conditionally write the single lock record in one readwrite
 * transaction. IndexedDB serializes those transactions for the same
 * same-origin database/store, so two fallback tabs cannot both acquire it.
 */
function tryAcquire(
  database: IDBDatabase,
  owner: string,
  now: number,
  leaseMs: number,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    let acquired = false;
    let settled = false;
    let transaction: IDBTransaction;
    try {
      transaction = database.transaction(STORE_NAME, "readwrite");
    } catch (error) {
      reject(error);
      return;
    }
    const finishError = () => {
      if (!settled) {
        settled = true;
        reject(transaction.error ?? new Error("IndexedDB mutex transaction failed."));
      }
    };
    transaction.onabort = finishError;
    transaction.onerror = finishError;
    transaction.oncomplete = () => {
      if (!settled) {
        settled = true;
        resolve(acquired);
      }
    };

    let store: IDBObjectStore;
    let getRequest: IDBRequest<LockRecord | undefined>;
    try {
      store = transaction.objectStore(STORE_NAME);
      getRequest = store.get(LOCK_KEY) as IDBRequest<LockRecord | undefined>;
    } catch (error) {
      reject(error);
      return;
    }
    getRequest.onerror = finishError;
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      // A stale owner is recoverable. Re-entrant acquisition by the same tab
      // only renews its own short lease; it does not authorize any session.
      if (!current || current.expiresAt <= now || current.owner === owner) {
        try {
          store.put({ key: LOCK_KEY, owner, expiresAt: now + leaseMs } satisfies LockRecord);
          acquired = true;
        } catch {
          try {
            transaction.abort();
          } catch {
            // The transaction error handler reports the controlled failure.
          }
        }
      }
    };
  });
}

/** Release the lock only when the durable record still belongs to this tab. */
function release(database: IDBDatabase, owner: string): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let transaction: IDBTransaction;
    try {
      transaction = database.transaction(STORE_NAME, "readwrite");
    } catch (error) {
      reject(error);
      return;
    }
    const finishError = () => {
      if (!settled) {
        settled = true;
        reject(transaction.error ?? new Error("IndexedDB mutex release failed."));
      }
    };
    transaction.onabort = finishError;
    transaction.onerror = finishError;
    transaction.oncomplete = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };

    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(LOCK_KEY) as IDBRequest<LockRecord | undefined>;
    getRequest.onerror = finishError;
    getRequest.onsuccess = () => {
      if (getRequest.result?.owner === owner) {
        store.delete(LOCK_KEY);
      }
    };
  });
}

/**
 * Serialize an idempotent first-party bootstrap call without localStorage.
 *
 * A disabled/denied IndexedDB implementation or bounded acquisition timeout
 * fails closed: `task` is never invoked, so no concurrent bootstrap request is
 * emitted by the fallback path.
 */
export async function withDeviceBootstrapIndexedDbMutex(
  task: () => Promise<void>,
  options: DeviceBootstrapMutexOptions,
): Promise<void> {
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? pause;
  const leaseMs = options.leaseMs ?? DEFAULT_LEASE_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retryMs = options.retryMs ?? DEFAULT_RETRY_MS;
  if (leaseMs <= 0 || timeoutMs <= 0 || retryMs <= 0) {
    throw new DeviceBootstrapUnavailableError("Invalid device bootstrap mutex settings.");
  }

  let database: IDBDatabase;
  try {
    database = await openMutexDatabase(indexedDbFactory(options.indexedDb));
  } catch (error) {
    if (error instanceof DeviceBootstrapUnavailableError) throw error;
    throw new DeviceBootstrapUnavailableError();
  }

  try {
    const deadline = now() + timeoutMs;
    while (now() <= deadline) {
      let acquired: boolean;
      try {
        acquired = await tryAcquire(database, options.owner, now(), leaseMs);
      } catch {
        throw new DeviceBootstrapUnavailableError();
      }
      if (acquired) {
        try {
          await task();
        } finally {
          // A release failure leaves only the short expiry. It must not let a
          // different owner delete the record; an expired owner is recoverable.
          try {
            await release(database, options.owner);
          } catch {
            // The bounded expiry remains the fail-safe recovery path.
          }
        }
        return;
      }

      const remaining = deadline - now();
      if (remaining <= 0) break;
      await sleep(Math.min(retryMs, remaining));
    }
    throw new DeviceBootstrapUnavailableError("Device bootstrap mutex acquisition timed out.");
  } finally {
    database.close();
  }
}
